// The Templates page (Session AUA, email system Phase 3, Items 6, 7 and 9): /office/reply-templates, a template's own page
// at /office/reply-templates/:id and the new template form at /office/reply-templates/new.
//
// The approved wording the email engine fills (Director decision D-MAIL-3: the AI picks and fills, it does not invent
// wording), the proposals queue the learning fills (each candidate shown WITH the sent replies it came from), and the reply
// style guide. Approving a TEMPLATE is not approving a REPLY: every reply made from one is still a Gmail draft a person
// sends, and the page says so wherever approval is offered.
//
// The server is server/src/modules/emailEngine/templates/templates.routes.ts. EVERY WRITE PREVIEWS FIRST (the route's own
// preview mode, which writes nothing) and is then made through a branded two-step confirm naming what will happen; nothing
// here uses window.confirm or window.alert. Built on the same rules as the Email engine page: a fixed-layout .ele-list-table
// that truncates (the TABLE RULE), client-side sorting via useSortableColumn + SortableHeader (the SORT RULE: the whole
// library is read at once, so a sort orders all of it), ConfirmButton / ConfirmDeleteModal (the two-step delete rule), no
// left stripes, theme tokens only. Every read is noCache (a read feeding an editable form must bypass the request cache).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type {
  ReplyCategoryOption,
  ReplyStyleGuide,
  ReplyTemplateCheck,
  ReplyTemplateDetail,
  ReplyTemplateExport,
  ReplyTemplateFields,
  ReplyTemplateImportResponse,
  ReplyTemplateListItem,
  ReplyTemplateListResponse,
  ReplyTemplateMiningResponse,
  ReplyTemplateMiningStatus,
  ReplyTemplateProposal,
  ReplyTemplateProposalStatus,
  ReplyTemplateSample,
  ReplyTemplateSlot,
  ReplyTemplateSourceReply,
} from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { useSortableColumn, sortRows, type SortAccessors } from "../../lib/useSortableColumn";
import { PageHeader } from "../../components/PageHeader";
import { RecordHeader } from "../../components/RecordHeader";
import { EmptyState } from "../../components/EmptyState";
import { StatusPill } from "../../components/StatusPill";
import { SortableHeader } from "../../components/SortableHeader";
import { ConfirmButton } from "../../components/ConfirmButton";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { ModalShell } from "../../components/ModalShell";
import { Button, ButtonLink } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { COMPANY_SIGNATURE_LABEL } from "./emailEngineUi";
import { formatDate, formatDateTime } from "../../lib/format";
import { statusEntry } from "../../lib/statusRegistry";
import {
  BESPOKE_MARKER,
  CheckFailures,
  ORIGIN_LABEL,
  PROPOSAL_KIND_LABEL,
  PROPOSAL_STATUS_LABEL,
  SENDER_LABEL,
  TEMPLATE_ATTACHMENTS,
  TEMPLATE_STATUS_LABEL,
  WordingWithSlots,
  percent,
  usageSentence,
} from "./replyTemplatesUi";

const BASE = "/office/reply-templates";
const sectionCls = "mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const inputCls =
  "w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green disabled:opacity-70";
const preCls = "whitespace-pre-wrap break-words rounded-ele border border-ele-hairline bg-ele-inset p-3 font-body text-sm text-ele-strong";
const chipCls = (on: boolean) =>
  `rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
    on ? "border-ele-green text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-strong"
  }`;
const linkBtn = "font-menu text-xs tracking-wide text-ele-blue transition hover:text-ele-strong disabled:opacity-50";
const confirmCls = "rounded-ele border border-ele-green px-4 py-2 font-menu text-xs tracking-wide text-ele-green disabled:opacity-50";
const AMBER_TEXT = "text-[var(--ele-amber-text)]";
const RED_TEXT = "text-ele-danger";
const AMBER_PANEL = "rounded-ele border border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-[var(--ele-amber-text)]";

const NOT_A_REPLY = "Approving a template is not approving a reply: every reply made from it is still a Gmail draft a person checks and sends.";

type Tab = "templates" | "proposals" | "style";
const TABS: { value: Tab; label: string }[] = [
  { value: "templates", label: "Templates" },
  { value: "proposals", label: "Proposals" },
  { value: "style", label: "Style guide" },
];

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

function StatusMessage({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <p role="status" className="mt-3 rounded-ele border border-ele-green bg-ele-field-inset px-3 py-2 text-sm text-ele-strong">
      {text}
    </p>
  );
}

function ErrorMessage({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <p role="alert" className={`mt-3 text-sm ${RED_TEXT}`}>
      {text}
    </p>
  );
}

// The reply categories (the server's one list), read from the library route so the client never keeps a second copy.
export function useReplyCategories(): ReplyCategoryOption[] {
  const [cats, setCats] = useState<ReplyCategoryOption[]>([]);
  useEffect(() => {
    let live = true;
    Promise.resolve()
      .then(() => api.replyTemplates.list({ limit: 1 }))
      .then((r) => live && setCats(r.categories ?? []))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);
  return cats;
}

function useSlots(): ReplyTemplateSlot[] {
  const [slots, setSlots] = useState<ReplyTemplateSlot[]>([]);
  useEffect(() => {
    let live = true;
    Promise.resolve()
      .then(() => api.replyTemplates.slots())
      .then((r) => live && setSlots(r.slots ?? []))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);
  return slots;
}

function slotLabels(slots: ReplyTemplateSlot[]): Record<string, string> {
  return Object.fromEntries(slots.map((s) => [s.name, s.label]));
}

// ---- The page ---------------------------------------------------------------------------------------------------------

export default function ReplyTemplatesPage() {
  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab: Tab = tabParam === "proposals" || tabParam === "style" ? tabParam : "templates";
  const [pendingProposals, setPendingProposals] = useState<number | null>(null);
  const [modal, setModal] = useState<"mine" | "export" | "import" | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  function selectTab(next: Tab) {
    const p = new URLSearchParams(params);
    if (next === "templates") p.delete("tab");
    else p.set("tab", next);
    setParams(p, { replace: true });
  }

  return (
    <div>
      <PageHeader
        title="Reply templates"
        subtitle="The approved wording the email engine fills when it drafts a reply. It picks a template and fills in the facts; it never invents the wording. Every reply is still a Gmail draft a person checks and sends."
        className="mb-4"
        actions={
          <>
            <ButtonLink to={`${BASE}/new`} size="sm">
              New template
            </ButtonLink>
            <Button variant="secondary" size="sm" onClick={() => setModal("mine")}>
              Mine proposals
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setModal("export")}>
              Export
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setModal("import")}>
              Import
            </Button>
          </>
        }
      />
      <div role="tablist" aria-label="Reply template sections" className="mb-4 flex flex-wrap gap-2 border-b border-ele-hairline pb-2">
        {TABS.map((t) => (
          <button key={t.value} type="button" role="tab" aria-selected={tab === t.value} onClick={() => selectTab(t.value)} className={chipCls(tab === t.value)}>
            {t.label}
            {t.value === "proposals" && pendingProposals != null && pendingProposals > 0 && <span className="ml-1 opacity-80">({pendingProposals})</span>}
          </button>
        ))}
      </div>
      {tab === "templates" && <TemplatesTab key={`t-${reloadKey}`} onCounts={(c) => setPendingProposals(c.pendingProposals)} />}
      {tab === "proposals" && <ProposalsTab key={`p-${reloadKey}`} onChanged={(n) => setPendingProposals(n)} />}
      {tab === "style" && <StyleGuideTab />}

      {modal === "mine" && <MiningModal onClose={() => setModal(null)} />}
      {modal === "export" && <ExportModal onClose={() => setModal(null)} />}
      {modal === "import" && (
        <ImportModal
          onClose={() => setModal(null)}
          onImported={() => {
            setReloadKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}

// ---- Templates --------------------------------------------------------------------------------------------------------

type StatusFilter = "any" | "approved" | "draft" | "retired";
const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "any", label: "Any status" },
  { value: "approved", label: "Approved" },
  { value: "draft", label: "Has a draft" },
  { value: "retired", label: "Retired" },
];

function TemplatesTab({ onCounts }: { onCounts: (c: ReplyTemplateListResponse["counts"]) => void }) {
  const navigate = useNavigate();
  const [data, setData] = useState<ReplyTemplateListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState<StatusFilter>("any");
  const [flagged, setFlagged] = useState(false);
  const [search, setSearch] = useState("");
  const sort = useSortableColumn("reply-templates");
  const seq = useRef(0);
  const onCountsRef = useRef(onCounts);
  onCountsRef.current = onCounts;

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await api.replyTemplates.list({ category: category || undefined, status, flagged, search: search.trim() || undefined, limit: 200 });
      if (mine !== seq.current) return;
      setData(res);
      onCountsRef.current(res.counts);
    } catch (err) {
      if (mine === seq.current) setError(friendlyApiError(err, "Could not load the templates."));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [category, status, flagged, search]);

  useEffect(() => {
    void load();
  }, [load]);

  const accessors: SortAccessors<ReplyTemplateListItem> = {
    title: (t) => t.title,
    category: (t) => t.categoryLabel,
    status: (t) => TEMPLATE_STATUS_LABEL[t.current.status],
    drafts: (t) => t.usage.drafts,
    goodRate: (t) => t.usage.goodRate,
    updated: (t) => t.updatedAt,
  };
  const items = data?.items ?? [];
  const rows = sortRows(items, accessors, sort);
  const counts = data?.counts;

  return (
    <div>
      {counts && (
        <p className="mb-3 text-sm text-ele-body" data-testid="template-counts">
          {plural(counts.keys, "template", "templates")}: {counts.approved} approved, {plural(counts.drafts, "with a draft", "with a draft")}
          {counts.flagged > 0 ? (
            <>
              , <span className={AMBER_TEXT}>{counts.flagged} flagged for review</span>
            </>
          ) : null}
          .
        </p>
      )}
      <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search title, key or wording..."
          aria-label="Search templates"
          className="w-full min-w-0 rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm text-ele-strong placeholder:text-ele-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ele-green lg:max-w-xs"
        />
        <select aria-label="Kind of email" value={category} onChange={(e) => setCategory(e.target.value)} className={`${inputCls} lg:max-w-xs`}>
          <option value="">Every kind of email</option>
          {(data?.categories ?? []).map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </select>
        <select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)} className={`${inputCls} lg:max-w-[12rem]`}>
          {STATUS_FILTERS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <button type="button" aria-pressed={flagged} onClick={() => setFlagged((f) => !f)} className={chipCls(flagged)}>
          Flagged for review only
        </button>
      </div>

      {loading && !data ? (
        <Loader label="Loading the templates..." rows={5} />
      ) : error ? (
        <div role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center">
          <p className="text-ele-strong">{error}</p>
          <button type="button" onClick={() => void load()} className={`mt-3 ${linkBtn}`}>
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          message={search.trim() || category || status !== "any" || flagged ? "No templates match these filters." : "No templates yet."}
          hint={
            !search.trim() && !category && status === "any" && !flagged
              ? "Write one with New template, accept one from the Proposals tab, or import the working-session document."
              : undefined
          }
        />
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              {/* At phone width only Template, Status and Good show, so they take the whole row (live check, Session AUA). */}
              <col className="w-[48%] md:w-[32%]" />
              <col className="max-md:hidden" style={{ width: "18%" }} />
              <col className="w-[32%] md:w-[16%]" />
              <col className="max-md:hidden" style={{ width: "14%" }} />
              <col className="w-[20%] md:w-[10%]" />
              <col className="max-md:hidden" style={{ width: "10%" }} />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <SortableHeader label="Template" colKey="title" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Kind of email" colKey="category" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Drafts" colKey="drafts" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Good" colKey="goodRate" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Updated" colKey="updated" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr
                  key={t.key}
                  data-testid={`template-${t.key}`}
                  className="cursor-pointer border-t border-ele-purple-accent transition hover:bg-ele-inset"
                  onClick={() => navigate(`${BASE}/${encodeURIComponent(t.current.id)}`)}
                >
                  <td className="px-3 py-2 align-top">
                    <div className="truncate font-heading text-ele-strong" title={t.title}>
                      {t.title}
                    </div>
                    <div className="truncate text-xs text-ele-body opacity-80" title={t.key}>
                      {t.key}
                    </div>
                    {t.flagged && (
                      <div className="mt-1 truncate" title={t.flagReason ?? undefined}>
                        <StatusPill word="Flagged for review" tone="amber" title={t.flagReason ?? undefined} />
                        {t.flagReason && <span className={`ml-2 text-xs ${AMBER_TEXT}`}>{t.flagReason}</span>}
                      </div>
                    )}
                  </td>
                  <td className="max-md:hidden truncate px-3 py-2 align-top text-ele-body" title={t.categoryLabel}>
                    {t.categoryLabel}
                  </td>
                  <td className="overflow-hidden px-3 py-2 align-top">
                    <div className="flex max-w-full flex-wrap items-center gap-x-1 gap-y-0.5" title={`${TEMPLATE_STATUS_LABEL[t.current.status]} v${t.current.version}`}>
                      <StatusPill word={TEMPLATE_STATUS_LABEL[t.current.status]} tone={statusEntry("replyTemplate", t.current.status).tone} />
                      <span className="text-xs text-ele-body">v{t.current.version}</span>
                    </div>
                    {t.draftVersion && t.current.status !== "draft" && <div className="mt-1 truncate text-xs text-ele-body">Draft v{t.draftVersion.version} waiting</div>}
                  </td>
                  <td className="max-md:hidden px-3 py-2 align-top text-ele-body" title={usageSentence(t.usage)}>
                    <div className="text-ele-strong">{t.usage.drafts}</div>
                    <div className="truncate text-xs">
                      {t.usage.unchanged} unchanged · {t.usage.smallEdits} small edits · {t.usage.heavilyRewritten} rewritten · {t.usage.discarded} discarded
                    </div>
                  </td>
                  <td className="truncate px-3 py-2 align-top text-ele-strong" title="Sent unchanged or with small edits, over its last uses">
                    {percent(t.usage.goodRate)}
                  </td>
                  <td className="max-md:hidden truncate px-3 py-2 align-top text-ele-body">{formatDate(t.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.total > items.length && (
        <p className="mt-2 text-xs text-ele-body">
          Showing the first {items.length} of {data.total}. Narrow the filters to find the rest.
        </p>
      )}
      <p className="mt-3 text-xs text-ele-body">
        &quot;Good&quot; is how many of its recent drafts were sent unchanged or with small edits. A template below half over its last
        uses is flagged for review, and the learning proposes a revision from how its drafts were rewritten.
      </p>
    </div>
  );
}

// ---- Proposals --------------------------------------------------------------------------------------------------------

const PROPOSAL_FILTERS: { value: ReplyTemplateProposalStatus | "all"; label: string }[] = [
  { value: "pending", label: "Waiting" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All" },
];

function ProposalsTab({ onChanged }: { onChanged: (pending: number) => void }) {
  const [status, setStatus] = useState<ReplyTemplateProposalStatus | "all">("pending");
  const [items, setItems] = useState<ReplyTemplateProposal[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const seq = useRef(0);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await api.replyTemplates.proposals({ status, limit: 200 });
      if (mine !== seq.current) return;
      setItems(res.items);
      setTotal(res.total);
      if (status === "pending") onChangedRef.current(res.total);
    } catch (err) {
      if (mine === seq.current) setError(friendlyApiError(err, "Could not load the proposals."));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <p className="mb-3 text-sm text-ele-body">
        Candidates the learning has proposed from the office&apos;s own sent replies, each with the replies it came from. Nothing becomes a
        template until you accept it, and nothing is used until a version is approved.
      </p>
      <div className="mb-3 flex flex-wrap gap-2">
        {PROPOSAL_FILTERS.map((f) => (
          <button key={f.value} type="button" aria-pressed={status === f.value} onClick={() => setStatus(f.value)} className={chipCls(status === f.value)}>
            {f.label}
          </button>
        ))}
      </div>
      <StatusMessage text={message} />
      {loading && items.length === 0 ? (
        <Loader label="Loading the proposals..." rows={4} />
      ) : error ? (
        <div role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center">
          <p className="text-ele-strong">{error}</p>
          <button type="button" onClick={() => void load()} className={`mt-3 ${linkBtn}`}>
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          message={status === "pending" ? "No proposals are waiting." : "No proposals match this filter."}
          hint={status === "pending" ? "Mine proposals asks the assistant for candidates from the office's sent replies." : undefined}
        />
      ) : (
        <div className="space-y-4">
          {items.map((p) => (
            <ProposalCard
              key={p.id}
              proposal={p}
              onDone={(text) => {
                setMessage(text);
                void load();
              }}
            />
          ))}
          {total > items.length && <p className="text-xs text-ele-body">Showing {items.length} of {total}.</p>}
        </div>
      )}
    </div>
  );
}

type AcceptPreview = Extract<Awaited<ReturnType<typeof api.replyTemplates.acceptProposal>>, { preview: true }>;
type RejectPreview = Extract<Awaited<ReturnType<typeof api.replyTemplates.rejectProposal>>, { preview: true }>;

function ProposalCard({ proposal: p, onDone }: { proposal: ReplyTemplateProposal; onDone: (message: string) => void }) {
  const [acceptPreview, setAcceptPreview] = useState<AcceptPreview | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [rejectPreview, setRejectPreview] = useState<RejectPreview | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function previewAccept(approve: boolean) {
    setBusy(approve ? "preview-approve" : "preview-accept");
    setError(null);
    setRejectPreview(null);
    setRejecting(false);
    try {
      const res = await api.replyTemplates.acceptProposal(p.id, { approve, preview: true });
      if (res.preview) setAcceptPreview(res);
    } catch (err) {
      setError(friendlyApiError(err, "The proposal could not be checked. Please try again."));
    } finally {
      setBusy(null);
    }
  }

  async function commitAccept() {
    if (!acceptPreview) return;
    setBusy("accept");
    setError(null);
    try {
      const res = await api.replyTemplates.acceptProposal(p.id, { approve: acceptPreview.approve, preview: false });
      if (!res.preview) {
        onDone(
          res.approved
            ? `Accepted and approved: ${res.template.key}, version ${res.template.version}. The engine can use it from now on.`
            : `Accepted as a draft: ${res.template.key}, version ${res.template.version}. Check it and approve it on its own page.`,
        );
      }
    } catch (err) {
      setError(friendlyApiError(err, "The proposal could not be accepted. Please try again."));
      setBusy(null);
    }
  }

  async function previewReject() {
    setBusy("preview-reject");
    setError(null);
    try {
      const res = await api.replyTemplates.rejectProposal(p.id, { preview: true, reason: reason.trim() || undefined });
      if (res.preview) setRejectPreview(res);
    } catch (err) {
      setError(friendlyApiError(err, "The proposal could not be checked. Please try again."));
    } finally {
      setBusy(null);
    }
  }

  async function commitReject() {
    setBusy("reject");
    setError(null);
    try {
      await api.replyTemplates.rejectProposal(p.id, { preview: false, reason: reason.trim() || undefined });
      onDone(`Rejected "${p.title}". It is kept as a record, so the same thing is not proposed again.`);
    } catch (err) {
      setError(friendlyApiError(err, "The proposal could not be rejected. Please try again."));
      setBusy(null);
    }
  }

  async function confirmDelete() {
    setBusy("delete");
    setDeleteError(null);
    try {
      await api.replyTemplates.removeProposal(p.id);
      setDeleting(false);
      onDone(`Deleted the proposal "${p.title}".`);
    } catch (err) {
      setDeleteError(friendlyApiError(err, "The proposal could not be deleted."));
      setBusy(null);
    }
  }

  const pending = p.status === "pending";
  const canDelete = p.status === "pending" || p.status === "rejected";

  return (
    <article className={sectionCls} data-testid={`proposal-${p.id}`} aria-label={`Proposal: ${p.title}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <StatusPill word={PROPOSAL_KIND_LABEL[p.kind] ?? p.kind} tone={p.kind === "new_template" ? "purple" : p.kind === "flagged_revision" ? "amber" : "blue"} />
        <StatusPill word={PROPOSAL_STATUS_LABEL[p.status] ?? p.status} tone={statusEntry("templateProposal", p.status).tone} />
        <span className="text-xs text-ele-body">
          {p.categoryLabel} · proposed {formatDate(p.createdAt)}
        </span>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="min-w-0">
          <h3 className="font-heading text-lg text-ele-strong">{p.title}</h3>
          <p className="text-xs text-ele-body">
            {p.key}
            {p.baseKey ? ` · from ${p.baseKey}, version ${p.baseVersion ?? "?"}` : ""}
          </p>
          <p className="mt-2 text-sm text-ele-body">
            <span className="text-ele-strong">When to use: </span>
            {p.whenToUse}
          </p>
          {p.whenNotToUse && (
            <p className="mt-1 text-sm text-ele-body">
              <span className="text-ele-strong">When not to use: </span>
              {p.whenNotToUse}
            </p>
          )}
          <p className={`${labelCls} mt-3`}>The proposed wording</p>
          <pre className={preCls} data-testid="proposal-body">
            <WordingWithSlots body={p.body} />
          </pre>
          {p.attachments.length > 0 && <p className="mt-1 text-xs text-ele-body">Attaches: {p.attachments.map(attachmentLabel).join(", ")}</p>}
          {p.rationale && <p className="mt-2 text-sm text-ele-body">Why: {p.rationale}</p>}
          {p.edits.length > 0 && (
            <div className="mt-3">
              <p className={labelCls}>The edit it carries</p>
              <ul className="space-y-2 text-sm">
                {p.edits.map((e, i) => (
                  <li key={i} className="rounded-ele border border-ele-hairline p-2">
                    {e.kind === "added" && (
                      <>
                        <span className="text-ele-strong">Added: </span>
                        {e.to}
                        {e.after ? <span className="text-ele-body"> (after &quot;{e.after}&quot;)</span> : null}
                      </>
                    )}
                    {e.kind === "removed" && (
                      <>
                        <span className="text-ele-strong">Removed: </span>
                        {e.from}
                      </>
                    )}
                    {e.kind === "reworded" && (
                      <>
                        <span className="text-ele-strong">Reworded: </span>&quot;{e.from}&quot; to &quot;{e.to}&quot;
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {p.checkFailures.length > 0 && (
            <div className="mt-3">
              <CheckFailures failures={p.checkFailures} heading="It does not pass every check yet (it can be accepted as a draft and fixed):" />
            </div>
          )}
          {p.status === "rejected" && p.rejectReason && <p className="mt-2 text-sm text-ele-body">Rejected: {p.rejectReason}</p>}
          {p.decidedAt && (
            <p className="mt-2 text-xs text-ele-body">
              {PROPOSAL_STATUS_LABEL[p.status]} {formatDateTime(p.decidedAt)}
              {p.decidedByName ? ` by ${p.decidedByName}` : ""}.
              {p.createdTemplateId && (
                <>
                  {" "}
                  <Link to={`${BASE}/${encodeURIComponent(p.createdTemplateId)}`} className="text-ele-blue hover:underline">
                    Open the template
                  </Link>
                </>
              )}
            </p>
          )}
        </div>
        <div className="min-w-0">
          <p className={labelCls}>The sent replies it came from</p>
          <SourceReplies replies={p.sourceReplies} />
        </div>
      </div>

      {(pending || canDelete) && (
        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-ele-hairline pt-3">
          {pending && (
            <>
              <Button variant="secondary" size="sm" onClick={() => void previewAccept(false)} disabled={busy != null}>
                {busy === "preview-accept" ? "Checking..." : "Accept as draft"}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => void previewAccept(true)} disabled={busy != null}>
                {busy === "preview-approve" ? "Checking..." : "Accept and approve"}
              </Button>
              <button
                type="button"
                className={linkBtn}
                disabled={busy != null}
                onClick={() => {
                  setRejecting(true);
                  setAcceptPreview(null);
                  setRejectPreview(null);
                }}
              >
                Reject
              </button>
            </>
          )}
          {canDelete && (
            <button
              type="button"
              className={`font-menu text-xs tracking-wide ${RED_TEXT} disabled:opacity-50`}
              disabled={busy != null}
              onClick={() => {
                setDeleteError(null);
                setDeleting(true);
              }}
            >
              Delete
            </button>
          )}
        </div>
      )}

      {acceptPreview && (
        <div className="mt-3 rounded-ele border border-ele-hairline p-3" data-testid="accept-preview">
          <p className="text-sm text-ele-strong">{acceptPreview.outcome}</p>
          {acceptPreview.check.failures.length > 0 && (
            <div className="mt-2">
              <CheckFailures failures={acceptPreview.check.failures} />
            </div>
          )}
          {acceptPreview.approve && <p className="mt-2 text-xs text-ele-body">{NOT_A_REPLY}</p>}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <ConfirmButton
              onConfirm={() => void commitAccept()}
              label={acceptPreview.approve ? "Accept and approve" : "Accept as draft"}
              confirmLabel={acceptPreview.approve ? `Approve ${acceptPreview.key}? Press again` : `Make ${acceptPreview.key} a draft? Press again`}
              busy={busy === "accept"}
              busyLabel="Accepting..."
              disabled={busy != null || (acceptPreview.approve && !acceptPreview.check.ok)}
              className={confirmCls}
              armedClassName="!text-ele-green font-semibold"
            />
            <button type="button" className={linkBtn} onClick={() => setAcceptPreview(null)} disabled={busy === "accept"}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {rejecting && (
        <div className="mt-3 rounded-ele border border-ele-hairline p-3" data-testid="reject-panel">
          <label className="block">
            <span className={labelCls}>Why (optional)</span>
            <input
              value={reason}
              maxLength={500}
              onChange={(e) => {
                setReason(e.target.value);
                setRejectPreview(null);
              }}
              aria-label="Why this proposal is rejected"
              className={inputCls}
            />
          </label>
          {!rejectPreview ? (
            <div className="mt-3 flex flex-wrap gap-3">
              <Button variant="ghost" size="sm" onClick={() => void previewReject()} disabled={busy != null}>
                {busy === "preview-reject" ? "Checking..." : "Preview the rejection"}
              </Button>
              <button type="button" className={linkBtn} onClick={() => setRejecting(false)}>
                Cancel
              </button>
            </div>
          ) : (
            <div className="mt-3">
              <p className="text-sm text-ele-strong">{rejectPreview.outcome}</p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <ConfirmButton
                  onConfirm={() => void commitReject()}
                  label="Reject this proposal"
                  confirmLabel="Reject it? Press again"
                  busy={busy === "reject"}
                  busyLabel="Rejecting..."
                  disabled={busy != null}
                  className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-strong"
                />
                <button
                  type="button"
                  className={linkBtn}
                  onClick={() => {
                    setRejecting(false);
                    setRejectPreview(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      <ErrorMessage text={error} />

      <ConfirmDeleteModal
        open={deleting}
        variant="destructive"
        title="Delete this proposal?"
        message={
          p.status === "rejected"
            ? "Deleting a rejected proposal removes the record that it was rejected, so the learning can propose the same thing again. To stop it coming back, leave it rejected."
            : "This removes the proposal. The learning can propose the same thing again later; reject it instead to keep a record so it is not."
        }
        confirmLabel="Delete proposal"
        busy={busy === "delete"}
        error={deleteError}
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          if (busy !== "delete") {
            setDeleting(false);
            setDeleteError(null);
          }
        }}
      />
    </article>
  );
}

function attachmentLabel(key: string): string {
  return TEMPLATE_ATTACHMENTS.find((a) => a.key === key)?.label ?? key;
}

function SourceReplies({ replies }: { replies: ReplyTemplateSourceReply[] }) {
  if (replies.length === 0) return <p className="text-sm text-ele-body">No source replies are recorded.</p>;
  return (
    <ul className="space-y-3" data-testid="source-replies">
      {replies.map((r) => (
        <li key={r.id} className="rounded-ele border border-ele-hairline p-3 text-sm">
          <p className="text-xs text-ele-body">
            {r.kind === "sent" ? "A reply sent from a draft" : "From the reply library"}
            {r.sentAt ? `, sent ${formatDate(r.sentAt)}` : ""}
          </p>
          {!r.available ? (
            <p className="mt-1 text-ele-body">This reply is no longer held (it was removed or erased).</p>
          ) : (
            <>
              {r.theyWrote && (
                <div className="mt-2">
                  <p className={labelCls}>They wrote</p>
                  <p className="whitespace-pre-wrap break-words text-ele-body">{r.theyWrote}</p>
                </div>
              )}
              {r.officeReplied && (
                <div className="mt-2">
                  <p className={labelCls}>The office replied</p>
                  <p className="whitespace-pre-wrap break-words text-ele-strong">{r.officeReplied}</p>
                </div>
              )}
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

// ---- Style guide ------------------------------------------------------------------------------------------------------

type StylePending = { text: string | null; bannedPhrases: string[]; isDefault: boolean; refusals: string[]; shownText: string | null };

function StyleGuideTab() {
  const [guide, setGuide] = useState<ReplyStyleGuide | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [text, setText] = useState("");
  const [banned, setBanned] = useState("");
  const [pending, setPending] = useState<StylePending | null>(null);
  const [busy, setBusy] = useState<"preview" | "save" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState("loading");
    try {
      const g = await api.replyTemplates.styleGuide();
      setGuide(g);
      setText(g.text);
      setBanned(g.bannedPhrases.join("\n"));
      setState("ready");
    } catch {
      setState("error");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const bannedList = (raw: string) =>
    raw
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);

  async function preview(nextText: string | null) {
    setBusy("preview");
    setError(null);
    setMessage(null);
    setPending(null);
    const phrases = bannedList(banned);
    try {
      const res = await api.replyTemplates.setStyleGuide({ text: nextText, bannedPhrases: phrases, preview: true });
      if (res.preview) setPending({ text: nextText, bannedPhrases: res.bannedPhrases, isDefault: res.isDefault, refusals: res.refusals, shownText: res.text });
    } catch (err) {
      setError(friendlyApiError(err, "The style guide could not be checked. Please try again."));
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    if (!pending) return;
    setBusy("save");
    setError(null);
    try {
      await api.replyTemplates.setStyleGuide({ text: pending.text, bannedPhrases: pending.bannedPhrases, preview: false });
      setMessage(pending.isDefault ? "Saved. The built-in guide is in use." : "Saved. Every draft from now on follows this guide.");
      setPending(null);
      await load();
    } catch (err) {
      setError(friendlyApiError(err, "The style guide could not be saved. Please try again."));
    } finally {
      setBusy(null);
    }
  }

  if (state === "loading") return <p className="text-sm text-ele-body">Loading the style guide...</p>;
  if (state === "error" || !guide) {
    return (
      <div role="alert" className="text-sm text-ele-strong">
        Could not load the style guide.{" "}
        <button type="button" className={linkBtn} onClick={() => void load()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <section className={sectionCls} aria-label="Reply style guide">
      <h2 className="font-heading text-lg text-ele-strong">Reply style guide</h2>
      <p className="mt-1 text-sm text-ele-body">
        How every reply should read. The assistant reads the guide whenever it writes wording (a free draft, a template&apos;s one bespoke
        sentence, or a proposal). The banned phrases are checked on every draft and every template: one fails a draft exactly as a wrong fact
        does. The fixed house rules (no slang, no emojis, British spelling, only 0203 576 4760) always apply and cannot be switched off here.
      </p>
      <p className="mt-2 text-xs text-ele-body">
        {guide.isDefault ? "The built-in guide is in use." : "The office's own guide is in use."}
        {guide.updatedAt ? ` Last changed ${formatDateTime(guide.updatedAt)}${guide.updatedByName ? ` by ${guide.updatedByName}` : ""}.` : ""}
      </p>
      <label className="mt-3 block">
        <span className={labelCls}>The guide</span>
        <textarea
          aria-label="The style guide"
          value={text}
          rows={12}
          maxLength={guide.limits.textMaxChars}
          onChange={(e) => {
            setText(e.target.value);
            setPending(null);
          }}
          className={`${inputCls} font-body`}
        />
        <span className="mt-1 block text-xs text-ele-body">
          {text.length.toLocaleString("en-GB")} of {guide.limits.textMaxChars.toLocaleString("en-GB")} characters
        </span>
      </label>
      <label className="mt-3 block">
        <span className={labelCls}>Banned phrases (one to a line)</span>
        <textarea
          aria-label="Banned phrases"
          value={banned}
          rows={5}
          onChange={(e) => {
            setBanned(e.target.value);
            setPending(null);
          }}
          className={inputCls}
        />
        <span className="mt-1 block text-xs text-ele-body">
          At most {guide.limits.bannedMax}, each between 2 and {guide.limits.bannedMaxChars} characters.
        </span>
      </label>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button variant="secondary" size="sm" onClick={() => void preview(text)} disabled={busy != null}>
          {busy === "preview" ? "Checking..." : "Preview"}
        </Button>
        {!guide.isDefault && (
          <button type="button" className={linkBtn} onClick={() => void preview(null)} disabled={busy != null}>
            Back to the built-in guide
          </button>
        )}
      </div>
      {pending && (
        <div className="mt-3 rounded-ele border border-ele-hairline p-3" data-testid="style-guide-preview">
          <p className="text-sm text-ele-strong">
            {pending.isDefault ? "The built-in guide will be used." : "This guide will be used."} {plural(pending.bannedPhrases.length, "banned phrase", "banned phrases")}
            {pending.bannedPhrases.length > 0 ? `: ${pending.bannedPhrases.join(", ")}` : ""}.
          </p>
          {pending.refusals.length > 0 ? (
            <ul role="alert" className={`mt-2 space-y-1 text-sm ${RED_TEXT}`}>
              {pending.refusals.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          ) : (
            <div className="mt-3">
              <ConfirmButton
                onConfirm={() => void save()}
                label={pending.isDefault ? "Use the built-in guide" : "Save the style guide"}
                confirmLabel="Every new draft will follow it. Press again"
                busy={busy === "save"}
                busyLabel="Saving..."
                disabled={busy != null}
                className={confirmCls}
                armedClassName="!text-ele-green font-semibold"
              />
            </div>
          )}
        </div>
      )}
      <ErrorMessage text={error} />
      <StatusMessage text={message} />
    </section>
  );
}

// ---- Mining, export and import ----------------------------------------------------------------------------------------

function MiningModal({ onClose }: { onClose: () => void }) {
  const [status, setStatus] = useState<ReplyTemplateMiningStatus | null>(null);
  const [preview, setPreview] = useState<Extract<ReplyTemplateMiningResponse, { preview: true }> | null>(null);
  const [busy, setBusy] = useState<"preview" | "start" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await api.replyTemplates.mining());
    } catch (err) {
      setError(friendlyApiError(err, "Could not read the mining status."));
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  async function askPreview() {
    setBusy("preview");
    setError(null);
    try {
      const res = await api.replyTemplates.mine({ preview: true });
      if (res.preview) setPreview(res);
    } catch (err) {
      setError(friendlyApiError(err, "Could not work out what to mine. Please try again."));
    } finally {
      setBusy(null);
    }
  }

  async function start() {
    if (!preview) return;
    setBusy("start");
    setError(null);
    try {
      await api.replyTemplates.mine({ preview: false, categories: preview.wouldMine.map((c) => c.category) });
      setMessage("Mining has started. It takes a few minutes; proposals appear on the Proposals tab as it works.");
      setPreview(null);
      await loadStatus();
    } catch (err) {
      setError(friendlyApiError(err, "Mining could not be started. Please try again."));
    } finally {
      setBusy(null);
    }
  }

  return (
    <ModalShell ariaLabel="Mine proposals" onClose={() => busy !== "start" && onClose()} width="max-w-[560px]">
      <h2 className="font-heading text-xl text-ele-strong">Mine proposals</h2>
      <p className="mt-2 text-sm text-ele-body">
        Asks the assistant for candidate templates from the office&apos;s own sent replies, one kind of email at a time. Proposals land on
        the Proposals tab; nothing is approved or used until you decide.
      </p>
      {status && (
        <p className="mt-3 text-sm text-ele-body" data-testid="mining-status">
          {status.running ? (
            <span className={AMBER_TEXT}>A run is going now (started {formatDateTime(status.startedAt)}).</span>
          ) : status.ranAt ? (
            `Last run ${formatDateTime(status.ranAt)}.`
          ) : (
            "Never run yet."
          )}
          {status.error ? <span className={`block ${AMBER_TEXT}`}>The last run hit a problem: {status.error}</span> : null}
        </p>
      )}
      {!preview ? (
        <div className="mt-4">
          <Button variant="secondary" size="sm" onClick={() => void askPreview()} disabled={busy != null || status?.running === true}>
            {busy === "preview" ? "Checking..." : "Preview what would be mined"}
          </Button>
        </div>
      ) : (
        <div className="mt-4 rounded-ele border border-ele-hairline p-3" data-testid="mining-preview">
          <p className="text-sm text-ele-strong">{preview.outcome}</p>
          {preview.wouldMine.length > 0 && (
            <ul className="mt-2 space-y-1 text-sm text-ele-body">
              {preview.wouldMine.map((c) => (
                <li key={c.category}>
                  {c.label}: {plural(c.replies, "sent reply", "sent replies")}
                </li>
              ))}
            </ul>
          )}
          {preview.wouldMine.length > 0 && (
            <div className="mt-3">
              <ConfirmButton
                onConfirm={() => void start()}
                label="Start mining"
                confirmLabel={`Mine ${plural(preview.wouldMine.length, "kind of email", "kinds of email")}? Press again`}
                busy={busy === "start"}
                busyLabel="Starting..."
                disabled={busy != null}
                className={confirmCls}
                armedClassName="!text-ele-green font-semibold"
              />
            </div>
          )}
        </div>
      )}
      <ErrorMessage text={error} />
      <StatusMessage text={message} />
      <div className="mt-5 flex justify-end">
        <Button variant="ghost" size="sm" onClick={onClose} disabled={busy === "start"}>
          Close
        </Button>
      </div>
    </ModalShell>
  );
}

/** Save text as a file in the browser (a Blob and a temporary link). Best effort: a browser without object URLs does nothing. */
export function downloadText(filename: string, text: string): boolean {
  try {
    if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") return false;
    const url = URL.createObjectURL(new Blob([text], { type: "text/markdown;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
    return true;
  } catch {
    return false;
  }
}

function ExportModal({ onClose }: { onClose: () => void }) {
  const [include, setInclude] = useState({ approved: true, drafts: false, proposals: true });
  const [doc, setDoc] = useState<ReplyTemplateExport | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function build() {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const parts = (Object.keys(include) as Array<keyof typeof include>).filter((k) => include[k]);
      setDoc(await api.replyTemplates.exportDocument(parts));
    } catch (err) {
      setError(friendlyApiError(err, "The document could not be put together. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Export templates" onClose={onClose} width="max-w-[560px]">
      <h2 className="font-heading text-xl text-ele-strong">Export</h2>
      <p className="mt-2 text-sm text-ele-body">
        One readable document for a template working session: each template and proposal with its kind of email, when to use it, the wording
        with its slots marked, and the sent replies it came from. Import it back once edited; nothing is approved by an import.
      </p>
      <fieldset className="mt-3 flex flex-wrap gap-4 text-sm text-ele-strong">
        <legend className="sr-only">What to include</legend>
        {(
          [
            ["approved", "Approved templates"],
            ["drafts", "Draft templates"],
            ["proposals", "Waiting proposals"],
          ] as const
        ).map(([k, label]) => (
          <label key={k} className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={include[k]}
              onChange={(e) => {
                setInclude((s) => ({ ...s, [k]: e.target.checked }));
                setDoc(null);
              }}
            />
            {label}
          </label>
        ))}
      </fieldset>
      <div className="mt-3">
        <Button variant="secondary" size="sm" onClick={() => void build()} disabled={busy || !Object.values(include).some(Boolean)}>
          {busy ? "Putting it together..." : "Preview the document"}
        </Button>
      </div>
      {doc && (
        <div className="mt-3" data-testid="export-preview">
          <p className="text-sm text-ele-strong">
            {plural(doc.counts.approved, "approved template", "approved templates")}, {plural(doc.counts.drafts, "draft", "drafts")} and{" "}
            {plural(doc.counts.proposals, "proposal", "proposals")}, as {doc.filename}.
          </p>
          <pre className={`${preCls} mt-2 max-h-72 overflow-y-auto text-xs`}>{doc.document}</pre>
          <div className="mt-3">
            <Button
              variant="primary"
              size="sm"
              onClick={() => setMessage(downloadText(doc.filename, doc.document) ? `Downloaded ${doc.filename}.` : "This browser could not save the file; copy the text above instead.")}
            >
              Download {doc.filename}
            </Button>
          </div>
        </div>
      )}
      <ErrorMessage text={error} />
      <StatusMessage text={message} />
      <div className="mt-5 flex justify-end">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>
    </ModalShell>
  );
}

const IMPORT_ACTION_LABEL: Record<string, string> = {
  create: "A new template (version 1, a draft)",
  new_version: "A new draft version",
  update_draft: "Changes the existing draft",
  unchanged: "Unchanged, nothing to do",
  refused: "Refused",
};

function ImportModal({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const [docText, setDocText] = useState("");
  const [plan, setPlan] = useState<ReplyTemplateImportResponse | null>(null);
  const [busy, setBusy] = useState<"check" | "import" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function readFile(file: File | undefined) {
    if (!file) return;
    try {
      const text = typeof file.text === "function" ? await file.text() : await new Response(file).text();
      setDocText(text);
      setPlan(null);
      setMessage(null);
    } catch {
      setError("That file could not be read.");
    }
  }

  async function check() {
    setBusy("check");
    setError(null);
    setMessage(null);
    try {
      setPlan(await api.replyTemplates.importDocument({ document: docText, preview: true }));
    } catch (err) {
      setError(friendlyApiError(err, "The document could not be checked. Please try again."));
    } finally {
      setBusy(null);
    }
  }

  async function commit() {
    if (!plan?.ok) return;
    setBusy("import");
    setError(null);
    try {
      const res = await api.replyTemplates.importDocument({ document: docText, preview: false });
      const n = res.written?.length ?? 0;
      setMessage(`Imported: ${plural(n, "draft written", "drafts written")}. Each is a draft to check and approve on its own page.`);
      setPlan(null);
      onImported();
    } catch (err) {
      if (err instanceof ApiError && err.body && Array.isArray((err.body as { blocks?: unknown }).blocks)) {
        setPlan({ ...(err.body as unknown as ReplyTemplateImportResponse), preview: true });
      }
      setError(friendlyApiError(err, "Nothing was imported. Please try again."));
    } finally {
      setBusy(null);
    }
  }

  const toWrite = plan ? plan.blocks.filter((b) => b.action !== "unchanged" && b.action !== "refused").length : 0;

  return (
    <ModalShell ariaLabel="Import templates" onClose={() => busy !== "import" && onClose()} width="max-w-[560px]">
      <h2 className="font-heading text-xl text-ele-strong">Import</h2>
      <p className="mt-2 text-sm text-ele-body">
        Paste the exported document, or choose the .md file. It is checked first (unknown slots and the tone guard included) and nothing is
        written unless every block passes. Everything imported is a draft; nothing is approved by an import.
      </p>
      <label className="mt-3 block">
        <span className={labelCls}>Choose a file</span>
        <input type="file" accept=".md,text/markdown,text/plain" aria-label="Choose the exported file" onChange={(e) => void readFile(e.target.files?.[0])} className="text-sm" />
      </label>
      <label className="mt-3 block">
        <span className={labelCls}>Or paste the document</span>
        <textarea
          aria-label="The document to import"
          value={docText}
          rows={8}
          onChange={(e) => {
            setDocText(e.target.value);
            setPlan(null);
          }}
          className={`${inputCls} font-mono text-xs`}
        />
      </label>
      <div className="mt-3">
        <Button variant="secondary" size="sm" onClick={() => void check()} disabled={busy != null || !docText.trim()}>
          {busy === "check" ? "Checking..." : "Check the document"}
        </Button>
      </div>
      {plan && (
        <div className="mt-3 rounded-ele border border-ele-hairline p-3" data-testid="import-preview">
          <ul className="space-y-2 text-sm">
            {plan.blocks.map((b) => (
              <li key={b.index} data-testid={`import-block-${b.index}`}>
                <span className="text-ele-strong">{b.key ?? b.heading}</span>
                {b.version != null ? `, version ${b.version}` : ""}:{" "}
                <span className={b.action === "refused" ? RED_TEXT : "text-ele-body"}>{IMPORT_ACTION_LABEL[b.action] ?? b.action}</span>
                {b.failures.length > 0 && (
                  <ul className="mt-1 space-y-1 pl-4 text-xs text-ele-body">
                    {b.failures.map((f, i) => (
                      <li key={i}>
                        {f.line ? <span className="text-ele-strong">&quot;{f.line}&quot;: </span> : null}
                        {f.detail}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
          {plan.ok ? (
            toWrite > 0 ? (
              <div className="mt-3">
                <ConfirmButton
                  onConfirm={() => void commit()}
                  label={`Import ${plural(toWrite, "draft", "drafts")}`}
                  confirmLabel={`Write ${plural(toWrite, "draft", "drafts")}? Press again`}
                  busy={busy === "import"}
                  busyLabel="Importing..."
                  disabled={busy != null}
                  className={confirmCls}
                  armedClassName="!text-ele-green font-semibold"
                />
              </div>
            ) : (
              <p className="mt-3 text-sm text-ele-body">Everything in the document is already on record. There is nothing to import.</p>
            )
          ) : (
            <p role="alert" className={`mt-3 text-sm ${RED_TEXT}`} data-testid="import-refused">
              Nothing can be imported until every block passes. Fix the refused blocks and check the document again.
            </p>
          )}
        </div>
      )}
      <ErrorMessage text={error} />
      <StatusMessage text={message} />
      <div className="mt-5 flex justify-end">
        <Button variant="ghost" size="sm" onClick={onClose} disabled={busy === "import"}>
          Close
        </Button>
      </div>
    </ModalShell>
  );
}

// ---- The template editor (new, edit a draft, make a new version) -------------------------------------------------------

export interface EditorPreview {
  check: ReplyTemplateCheck;
  sample?: ReplyTemplateSample;
  /** A plain sentence the route said about the save (the version it would create, or why it cannot). */
  note?: string | null;
  /** Why it cannot be saved at all (a key already used, a draft already waiting). */
  refusal?: string | null;
}

type EditorFields = Required<Pick<ReplyTemplateFields, "category" | "title" | "whenToUse" | "whenNotToUse" | "body" | "attachments" | "allowBespokeSentence">> & {
  key: string;
};

function fieldsSignature(f: EditorFields): string {
  return JSON.stringify(f);
}

function TemplateEditor({
  mode,
  initial,
  categories,
  slots,
  onPreview,
  onSave,
  onCancel,
}: {
  mode: "new" | "edit" | "newVersion";
  initial: EditorFields;
  categories: ReplyCategoryOption[];
  slots: ReplyTemplateSlot[];
  onPreview: (f: EditorFields) => Promise<EditorPreview>;
  onSave: (f: EditorFields) => Promise<void>;
  onCancel?: () => void;
}) {
  const [f, setF] = useState<EditorFields>(initial);
  const [preview, setPreview] = useState<{ sig: string; result: EditorPreview } | null>(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slotPick, setSlotPick] = useState("");
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const seq = useRef(0);
  const onPreviewRef = useRef(onPreview);
  onPreviewRef.current = onPreview;
  const sig = fieldsSignature(f);
  const labels = useMemo(() => slotLabels(slots), [slots]);

  function set<K extends keyof EditorFields>(k: K, v: EditorFields[K]) {
    setF((s) => ({ ...s, [k]: v }));
  }

  const runPreview = useCallback(async (fields: EditorFields) => {
    const mine = ++seq.current;
    setChecking(true);
    setError(null);
    try {
      const result = await onPreviewRef.current(fields);
      if (mine === seq.current) setPreview({ sig: fieldsSignature(fields), result });
    } catch (err) {
      if (mine === seq.current) setError(friendlyApiError(err, "The wording could not be checked. Please try again."));
    } finally {
      if (mine === seq.current) setChecking(false);
    }
  }, []);

  // A live preview: the route's own preview mode, a moment after typing stops. It writes nothing.
  useEffect(() => {
    if (!f.body.trim() || !f.title.trim() || !f.category || (mode === "new" && !f.key.trim())) return;
    const t = setTimeout(() => void runPreview(f), 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, runPreview]);

  function insertAtCursor(text: string) {
    const el = bodyRef.current;
    const body = f.body;
    const start = el?.selectionStart ?? body.length;
    const end = el?.selectionEnd ?? body.length;
    const next = body.slice(0, start) + text + body.slice(end);
    set("body", next);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.selectionStart = el.selectionEnd = start + text.length;
    });
  }

  function toggleBespoke(on: boolean) {
    if (on) {
      if (!f.body.includes(BESPOKE_MARKER)) {
        const el = bodyRef.current;
        const pos = el?.selectionStart ?? f.body.length;
        const before = f.body.slice(0, pos);
        const after = f.body.slice(pos);
        const lead = before.length === 0 || before.endsWith("\n") ? "" : "\n";
        const trail = after.length === 0 || after.startsWith("\n") ? "" : "\n";
        setF((s) => ({ ...s, allowBespokeSentence: true, body: `${before}${lead}${BESPOKE_MARKER}${trail}${after}` }));
      } else {
        set("allowBespokeSentence", true);
      }
    } else {
      setF((s) => ({
        ...s,
        allowBespokeSentence: false,
        body: s.body
          .split("\n")
          .filter((line) => line.trim() !== BESPOKE_MARKER)
          .join("\n")
          .split(BESPOKE_MARKER)
          .join(""),
      }));
    }
  }

  const current = preview && preview.sig === sig ? preview.result : null;
  const hard = current ? current.check.failures.some((x) => x.hard) : false;
  const canSave = Boolean(current) && !hard && !current?.refusal && !checking;

  async function save() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(f);
    } catch (err) {
      setError(friendlyApiError(err, "It could not be saved. Please try again."));
      setSaving(false);
    }
  }

  const saveLabel = mode === "new" ? "Create the draft template" : mode === "edit" ? "Save the draft" : "Create the new version";

  return (
    <section className={sectionCls} aria-label={mode === "new" ? "New template" : mode === "edit" ? "Edit the draft" : "Make a new version"}>
      <div className="grid gap-3 md:grid-cols-2">
        {mode === "new" && (
          <label className="block">
            <span className={labelCls}>Key</span>
            <input
              aria-label="Key"
              value={f.key}
              maxLength={80}
              onChange={(e) => set("key", e.target.value.toLowerCase())}
              placeholder="maintenance_quote.intake"
              className={inputCls}
            />
            <span className="mt-1 block text-xs text-ele-body">Lower case words joined by dots, such as maintenance_quote.intake. It never changes.</span>
          </label>
        )}
        <label className="block">
          <span className={labelCls}>Kind of email</span>
          <select aria-label="Kind of email" value={f.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
            <option value="">Choose...</option>
            {categories.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block md:col-span-2">
          <span className={labelCls}>Title</span>
          <input aria-label="Title" value={f.title} maxLength={120} onChange={(e) => set("title", e.target.value)} className={inputCls} />
        </label>
        <label className="block">
          <span className={labelCls}>When to use</span>
          <textarea aria-label="When to use" value={f.whenToUse} rows={3} maxLength={1000} onChange={(e) => set("whenToUse", e.target.value)} className={inputCls} />
        </label>
        <label className="block">
          <span className={labelCls}>When not to use (optional)</span>
          <textarea aria-label="When not to use" value={f.whenNotToUse} rows={3} maxLength={1000} onChange={(e) => set("whenNotToUse", e.target.value)} className={inputCls} />
        </label>
      </div>

      <div className="mt-3">
        <div className="mb-1 flex flex-wrap items-end justify-between gap-2">
          <span className={labelCls}>Wording</span>
          <div className="flex flex-wrap items-center gap-2">
            <select aria-label="Insert a slot" value={slotPick} onChange={(e) => setSlotPick(e.target.value)} className={`${inputCls} max-w-xs py-1 text-xs`}>
              <option value="">Insert a slot...</option>
              {slots.map((s) => (
                <option key={s.name} value={s.name}>
                  {s.label} {`{${s.name}}`}
                  {s.needsCustomerRecords ? " (verified customers only)" : ""}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={linkBtn}
              disabled={!slotPick}
              onClick={() => {
                insertAtCursor(`{${slotPick}}`);
                setSlotPick("");
              }}
            >
              Insert
            </button>
          </div>
        </div>
        <textarea
          ref={bodyRef}
          aria-label="Wording"
          value={f.body}
          rows={12}
          maxLength={6000}
          onChange={(e) => set("body", e.target.value)}
          className={`${inputCls} font-body`}
        />
        {slotPick && <p className="mt-1 text-xs text-ele-body">{slots.find((s) => s.name === slotPick)?.resolvesWhen}</p>}
        <p className="mt-1 text-xs text-ele-body">
          Start with {"{greeting}"}. Every price, date, reference and name comes from a slot, never typed in. No sign-off: &quot;Kind
          regards,&quot; and the {COMPANY_SIGNATURE_LABEL} are added to every draft.
        </p>
      </div>

      <div className="mt-3 flex flex-wrap gap-6 text-sm text-ele-strong">
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={f.allowBespokeSentence} onChange={(e) => toggleBespoke(e.target.checked)} />
          Allow one bespoke sentence (the engine may add one sentence answering anything specific, where {BESPOKE_MARKER} stands)
        </label>
        {TEMPLATE_ATTACHMENTS.map((a) => (
          <label key={a.key} className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={f.attachments.includes(a.key)}
              onChange={(e) => set("attachments", e.target.checked ? [...f.attachments, a.key] : f.attachments.filter((x) => x !== a.key))}
            />
            Attach the {a.label}
          </label>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => void runPreview(f)} disabled={checking || saving}>
          {checking ? "Checking..." : "Check now"}
        </Button>
        {onCancel && (
          <button type="button" className={linkBtn} onClick={onCancel} disabled={saving}>
            Cancel
          </button>
        )}
      </div>

      {current && (
        <div className="mt-4 rounded-ele border border-ele-hairline p-3" data-testid="editor-preview">
          {current.note && <p className="mb-2 text-sm text-ele-strong">{current.note}</p>}
          {current.refusal && <p className={`mb-2 text-sm ${RED_TEXT}`}>{current.refusal}</p>}
          {current.check.ok ? (
            <p className="text-sm text-ele-green">Passes every check.</p>
          ) : (
            <>
              <CheckFailures failures={current.check.failures} />
              {!hard && <p className={`mt-2 text-xs ${AMBER_TEXT}`}>It can be saved as a draft, but it cannot be approved until every check passes.</p>}
            </>
          )}
          {current.check.usableFor.length > 0 && (
            <p className="mt-2 text-xs text-ele-body">Can be used for {current.check.usableFor.map((u) => SENDER_LABEL[u]).join(" and ")}.</p>
          )}
          {current.sample?.text && (
            <div className="mt-3">
              <p className={labelCls}>Filled with a sample message</p>
              <pre className={preCls}>{current.sample.text}</pre>
              <p className="mt-1 text-xs text-ele-body">Every draft then ends with &quot;Kind regards,&quot; and the {COMPANY_SIGNATURE_LABEL}.</p>
            </div>
          )}
          {f.body.trim() && (
            <div className="mt-3">
              <p className={labelCls}>The wording with its slots marked</p>
              <pre className={preCls}>
                <WordingWithSlots body={f.body} labels={labels} />
              </pre>
            </div>
          )}
          <div className="mt-3">
            <ConfirmButton
              onConfirm={() => void save()}
              label={saveLabel}
              confirmLabel={`${saveLabel}? Press again`}
              busy={saving}
              busyLabel="Saving..."
              disabled={!canSave || saving}
              className={confirmCls}
              armedClassName="!text-ele-green font-semibold"
            />
          </div>
        </div>
      )}
      {!current && !checking && <p className="mt-3 text-xs text-ele-body">Fill in the kind of email, title and wording; it is checked as you type, and saved only after a check.</p>}
      <ErrorMessage text={error} />
    </section>
  );
}

const EMPTY_FIELDS: EditorFields = { key: "", category: "", title: "", whenToUse: "", whenNotToUse: "", body: "{greeting}\n\n", attachments: [], allowBespokeSentence: false };

function toFields(f: EditorFields, withKey: boolean): ReplyTemplateFields {
  return {
    ...(withKey ? { key: f.key.trim() } : {}),
    category: f.category,
    title: f.title,
    whenToUse: f.whenToUse,
    whenNotToUse: f.whenNotToUse,
    body: f.body,
    attachments: f.attachments,
    allowBespokeSentence: f.allowBespokeSentence,
  };
}

// ---- The new template form --------------------------------------------------------------------------------------------

export function ReplyTemplateNewPage() {
  const navigate = useNavigate();
  const categories = useReplyCategories();
  const slots = useSlots();
  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <RecordHeader title="New template" backTo={BASE} backLabel="Back to the templates" subtitle="A new template starts as a draft. Approve it on its own page once every check passes." className="mb-4" />
      <TemplateEditor
        mode="new"
        initial={EMPTY_FIELDS}
        categories={categories}
        slots={slots}
        onPreview={async (f) => {
          const res = await api.replyTemplates.create({ ...toFields(f, true), preview: true });
          if (!res.preview) throw new Error("unexpected");
          return {
            check: res.check,
            sample: res.sample,
            note: `It will be ${res.wouldCreate.key}, version ${res.wouldCreate.version}, a draft.`,
            refusal: res.keyExists ? `The key ${res.wouldCreate.key} is already used. Choose another, or make a new version of that template from its own page.` : null,
          };
        }}
        onSave={async (f) => {
          const res = await api.replyTemplates.create({ ...toFields(f, true), preview: false });
          if (!res.preview) navigate(`${BASE}/${encodeURIComponent(res.template.id)}`);
        }}
        onCancel={() => navigate(BASE)}
      />
    </div>
  );
}

// ---- One template -----------------------------------------------------------------------------------------------------

type ApprovePreview = Extract<Awaited<ReturnType<typeof api.replyTemplates.approve>>, { preview: true }>;
type RetirePreview = Extract<Awaited<ReturnType<typeof api.replyTemplates.retire>>, { preview: true }>;

export function ReplyTemplateDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const categories = useReplyCategories();
  const slots = useSlots();
  const labels = useMemo(() => slotLabels(slots), [slots]);
  const [detail, setDetail] = useState<ReplyTemplateDetail | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound" | "error">("loading");
  const [editing, setEditing] = useState<"edit" | "newVersion" | null>(null);
  const [approvePreview, setApprovePreview] = useState<ApprovePreview | null>(null);
  const [retiring, setRetiring] = useState(false);
  const [retireReason, setRetireReason] = useState("");
  const [retirePreview, setRetirePreview] = useState<RetirePreview | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setState("loading");
      try {
        setDetail(await api.replyTemplates.get(id));
        setState("ready");
      } catch (err) {
        if (!opts?.silent) setState(err instanceof ApiError && err.status === 404 ? "notfound" : "error");
      }
    },
    [id],
  );

  useEffect(() => {
    setEditing(null);
    setApprovePreview(null);
    setRetiring(false);
    setRetirePreview(null);
    setMessage(null);
    setError(null);
    void load();
  }, [load]);

  if (state === "loading") return <Loader shape="panel" className="p-6" />;
  if (state === "notfound") return <p className="p-6 text-center text-ele-body">That template could not be found.</p>;
  if (state === "error" || !detail) return <p role="alert" className="p-6 text-center text-ele-strong">Could not load this template. Please try again.</p>;

  const t = detail.template;
  const initial: EditorFields = {
    key: t.key,
    category: t.category,
    title: t.title,
    whenToUse: t.whenToUse,
    whenNotToUse: t.whenNotToUse ?? "",
    body: t.body,
    attachments: t.attachments,
    allowBespokeSentence: t.allowBespokeSentence,
  };

  async function previewApprove() {
    setBusy("preview-approve");
    setError(null);
    try {
      const res = await api.replyTemplates.approve(t.id, { preview: true });
      if (res.preview) setApprovePreview(res);
    } catch (err) {
      setError(friendlyApiError(err, "The approval could not be checked. Please try again."));
    } finally {
      setBusy(null);
    }
  }

  async function commitApprove() {
    setBusy("approve");
    setError(null);
    try {
      const res = await api.replyTemplates.approve(t.id, { preview: false });
      if (!res.preview) {
        setApprovePreview(null);
        setMessage(
          `Approved: version ${res.template.version} is the wording the engine fills${res.retired ? `, and version ${res.retired.version} is retired` : ""}.`,
        );
        await load({ silent: true });
      }
    } catch (err) {
      setError(friendlyApiError(err, "It could not be approved. Please try again."));
    } finally {
      setBusy(null);
    }
  }

  async function previewRetire() {
    setBusy("preview-retire");
    setError(null);
    try {
      const res = await api.replyTemplates.retire(t.id, { preview: true, reason: retireReason.trim() || undefined });
      if (res.preview) setRetirePreview(res);
    } catch (err) {
      setError(friendlyApiError(err, "The retirement could not be checked. Please try again."));
    } finally {
      setBusy(null);
    }
  }

  async function commitRetire() {
    setBusy("retire");
    setError(null);
    try {
      await api.replyTemplates.retire(t.id, { preview: false, reason: retireReason.trim() || undefined });
      setRetiring(false);
      setRetirePreview(null);
      setMessage(`Retired version ${t.version}. The engine no longer uses it; a new version can be made from it.`);
      await load({ silent: true });
    } catch (err) {
      setError(friendlyApiError(err, "It could not be retired. Please try again."));
    } finally {
      setBusy(null);
    }
  }

  async function confirmDelete() {
    setBusy("delete");
    setDeleteError(null);
    try {
      await api.replyTemplates.remove(t.id);
      const other = detail?.versions.find((v) => v.id !== t.id);
      navigate(other ? `${BASE}/${encodeURIComponent(other.id)}` : BASE);
    } catch (err) {
      setDeleteError(friendlyApiError(err, "It could not be deleted."));
      setBusy(null);
    }
  }

  function openRetire() {
    setRetiring(true);
    setRetirePreview(null);
    setApprovePreview(null);
    setEditing(null);
  }

  const failureCount = detail.check.failures.length;

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <RecordHeader
        title={t.title}
        backTo={BASE}
        backLabel="Back to the templates"
        status={<StatusPill word={`${TEMPLATE_STATUS_LABEL[t.status]}, version ${t.version}`} tone={statusEntry("replyTemplate", t.status).tone} />}
        subtitle={`${t.key} · ${t.categoryLabel}`}
        className="mb-4"
      />

      {detail.usage.flagged && (
        <p className={`mb-4 ${AMBER_PANEL}`} data-testid="flagged-banner">
          Flagged for review. {detail.usage.flagReason}
        </p>
      )}
      <StatusMessage text={message} />

      {/* The controls, by status. Every write previews first and is then confirmed. */}
      <section className={sectionCls} aria-label="What you can do">
        <div className="flex flex-wrap items-center gap-3">
          {t.status === "draft" && (
            <>
              <Button variant="secondary" size="sm" onClick={() => setEditing(editing === "edit" ? null : "edit")} disabled={busy != null}>
                Edit
              </Button>
              <Button variant="primary" size="sm" onClick={() => void previewApprove()} disabled={busy != null || !detail.check.ok} title={detail.check.ok ? undefined : "Every check must pass first"}>
                {busy === "preview-approve" ? "Checking..." : "Approve"}
              </Button>
            </>
          )}
          {(t.status === "approved" || t.status === "retired") && (
            <Button variant="secondary" size="sm" onClick={() => setEditing(editing === "newVersion" ? null : "newVersion")} disabled={busy != null}>
              Make a new version
            </Button>
          )}
          {t.status === "approved" && (
            <Button variant="ghost" size="sm" onClick={openRetire} disabled={busy != null}>
              Retire
            </Button>
          )}
          {detail.canDelete && (
            <button
              type="button"
              className={`font-menu text-xs tracking-wide ${RED_TEXT} disabled:opacity-50`}
              disabled={busy != null}
              onClick={() => {
                setDeleteError(null);
                setDeleting(true);
              }}
            >
              Delete
            </button>
          )}
        </div>
        {t.status === "draft" && !detail.check.ok && (
          <p className={`mt-3 text-sm ${RED_TEXT}`} data-testid="approve-blocked">
            It cannot be approved until every check passes: {plural(failureCount, "check fails", "checks fail")} (listed under Checks below).
          </p>
        )}
        {!detail.canDelete && detail.deleteRefusal && (
          <p className="mt-3 text-sm text-ele-body" data-testid="delete-refusal">
            {detail.deleteRefusal}
            {t.status === "approved" && (
              <>
                {" "}
                <button type="button" className={linkBtn} onClick={openRetire}>
                  Retire it instead
                </button>
              </>
            )}
          </p>
        )}
        <p className="mt-3 text-xs text-ele-body">{NOT_A_REPLY}</p>

        {approvePreview && (
          <div className="mt-3 rounded-ele border border-ele-hairline p-3" data-testid="approve-preview">
            <p className="text-sm text-ele-strong">{approvePreview.outcome}</p>
            {approvePreview.check.failures.length > 0 && (
              <div className="mt-2">
                <CheckFailures failures={approvePreview.check.failures} />
              </div>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <ConfirmButton
                onConfirm={() => void commitApprove()}
                label={`Approve version ${t.version}`}
                confirmLabel={approvePreview.replaces ? `Approve, retiring version ${approvePreview.replaces.version}? Press again` : "Approve it? Press again"}
                busy={busy === "approve"}
                busyLabel="Approving..."
                disabled={busy != null || !approvePreview.check.ok}
                className={confirmCls}
                armedClassName="!text-ele-green font-semibold"
              />
              <button type="button" className={linkBtn} onClick={() => setApprovePreview(null)} disabled={busy === "approve"}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {retiring && (
          <div className="mt-3 rounded-ele border border-ele-hairline p-3" data-testid="retire-panel">
            <label className="block">
              <span className={labelCls}>Why (optional)</span>
              <input
                aria-label="Why this version is retired"
                value={retireReason}
                maxLength={500}
                onChange={(e) => {
                  setRetireReason(e.target.value);
                  setRetirePreview(null);
                }}
                className={inputCls}
              />
            </label>
            {!retirePreview ? (
              <div className="mt-3 flex flex-wrap gap-3">
                <Button variant="ghost" size="sm" onClick={() => void previewRetire()} disabled={busy != null}>
                  {busy === "preview-retire" ? "Checking..." : "Preview the retirement"}
                </Button>
                <button type="button" className={linkBtn} onClick={() => setRetiring(false)}>
                  Cancel
                </button>
              </div>
            ) : (
              <div className="mt-3">
                <p className={`text-sm ${retirePreview.refusal ? RED_TEXT : "text-ele-strong"}`}>{retirePreview.outcome}</p>
                {!retirePreview.refusal && (
                  <div className="mt-3">
                    <ConfirmButton
                      onConfirm={() => void commitRetire()}
                      label={`Retire version ${t.version}`}
                      confirmLabel="Stop the engine using it? Press again"
                      busy={busy === "retire"}
                      busyLabel="Retiring..."
                      disabled={busy != null}
                      className="rounded-ele border border-amber-500 px-4 py-2 font-menu text-xs tracking-wide text-[var(--ele-amber-text)]"
                      armedClassName="font-semibold"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <ErrorMessage text={error} />
      </section>

      {editing && (
        <TemplateEditor
          key={`${t.id}-${editing}`}
          mode={editing}
          initial={initial}
          categories={categories.length ? categories : [{ key: t.category, label: t.categoryLabel }]}
          slots={slots}
          onCancel={() => setEditing(null)}
          onPreview={async (f) => {
            if (editing === "edit") {
              const res = await api.replyTemplates.update(t.id, { ...toFields(f, false), preview: true });
              if (!res.preview) throw new Error("unexpected");
              return { check: res.check, sample: res.sample, note: `This changes the draft, version ${t.version}.` };
            }
            const res = await api.replyTemplates.newVersion(t.id, { ...toFields(f, false), preview: true });
            if (!res.preview) throw new Error("unexpected");
            return { check: res.check, note: `It will be version ${res.wouldCreate.version}, a draft.`, refusal: res.refusal };
          }}
          onSave={async (f) => {
            if (editing === "edit") {
              await api.replyTemplates.update(t.id, { ...toFields(f, false), preview: false });
              setEditing(null);
              setMessage("Saved the draft.");
              await load({ silent: true });
              return;
            }
            const res = await api.replyTemplates.newVersion(t.id, { ...toFields(f, false), preview: false });
            if (!res.preview) {
              setEditing(null);
              navigate(`${BASE}/${encodeURIComponent(res.template.id)}`);
            }
          }}
        />
      )}

      <section className={sectionCls} aria-label="The wording">
        <h2 className="mb-2 font-heading text-lg text-ele-strong">The wording</h2>
        <dl className="mb-3 grid gap-3 text-sm sm:grid-cols-2">
          <Field label="When to use" value={t.whenToUse} />
          <Field label="When not to use" value={t.whenNotToUse || "-"} />
          <Field label="Attaches" value={t.attachments.length ? t.attachments.map(attachmentLabel).join(", ") : "Nothing"} />
          <Field label="Bespoke sentence" value={t.allowBespokeSentence ? "Allowed: the engine may add one sentence where marked" : "Not allowed"} />
          <Field label="Can be used for" value={detail.check.usableFor.length ? detail.check.usableFor.map((u) => SENDER_LABEL[u]).join(" and ") : "Nobody until every slot can resolve"} />
          <Field label="Where it came from" value={`${ORIGIN_LABEL[t.origin] ?? t.origin}${t.createdByName ? `, by ${t.createdByName}` : ""}, ${formatDate(t.createdAt)}`} />
        </dl>
        <pre className={preCls} data-testid="template-wording">
          <WordingWithSlots body={t.body} labels={labels} />
        </pre>
        {detail.check.requiredSlots.length > 0 && (
          <div className="mt-3">
            <p className={labelCls}>The slots it needs (each must resolve from the facts, or the template is not used)</p>
            <ul className="space-y-1 text-sm">
              {detail.check.requiredSlots.map((s) => {
                const def = slots.find((x) => x.name === s);
                return (
                  <li key={s} className="text-ele-body">
                    <span className="font-menu text-xs text-ele-blue">{`{${s}}`}</span> {def ? `${def.label}: ${def.resolvesWhen}` : ""}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </section>

      <section className={sectionCls} aria-label="Sample">
        <h2 className="mb-2 font-heading text-lg text-ele-strong">How a draft reads</h2>
        <p className="mb-2 text-xs text-ele-body">
          {detail.sample.sample === "examples"
            ? "Filled with example values."
            : `Filled from a sample message from ${SENDER_LABEL[detail.sample.sample]}.`}{" "}
          Every draft then ends with &quot;Kind regards,&quot; and the {COMPANY_SIGNATURE_LABEL}.
        </p>
        {detail.sample.text ? <pre className={preCls} data-testid="template-sample">{detail.sample.text}</pre> : <p className="text-sm text-ele-body">It cannot be filled as it stands.</p>}
      </section>

      <section className={sectionCls} aria-label="Checks">
        <h2 className="mb-2 font-heading text-lg text-ele-strong">Checks</h2>
        {detail.check.ok ? <p className="text-sm text-ele-green" data-testid="checks-ok">Passes every check.</p> : <CheckFailures failures={detail.check.failures} />}
      </section>

      <section className={sectionCls} aria-label="How it is doing">
        <h2 className="mb-2 font-heading text-lg text-ele-strong">How this version is doing</h2>
        <UsageGrid usage={detail.usage} />
      </section>

      <section className={sectionCls} aria-label="Version history">
        <h2 className="mb-2 font-heading text-lg text-ele-strong">Versions</h2>
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              <col style={{ width: "12%" }} />
              <col style={{ width: "18%" }} />
              <col className="max-md:hidden" style={{ width: "30%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "12%" }} />
              <col className="max-md:hidden" style={{ width: "14%" }} />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <th className="px-3 py-2">Version</th>
                <th className="px-3 py-2">Status</th>
                <th className="max-md:hidden px-3 py-2">Approved or retired</th>
                <th className="px-3 py-2">Drafts</th>
                <th className="px-3 py-2">Good</th>
                <th className="max-md:hidden px-3 py-2">Review</th>
              </tr>
            </thead>
            <tbody>
              {detail.versions.map((v) => (
                <tr
                  key={v.id}
                  data-testid={`version-${v.version}`}
                  className={`cursor-pointer border-t border-ele-purple-accent transition hover:bg-ele-inset ${v.id === t.id ? "bg-ele-inset" : ""}`}
                  onClick={() => navigate(`${BASE}/${encodeURIComponent(v.id)}`)}
                >
                  <td className="truncate px-3 py-2 text-ele-strong">
                    {v.version}
                    {v.id === t.id ? " (this one)" : ""}
                  </td>
                  <td className="px-3 py-2">
                    <StatusPill word={TEMPLATE_STATUS_LABEL[v.status]} tone={statusEntry("replyTemplate", v.status).tone} />
                  </td>
                  <td className="max-md:hidden truncate px-3 py-2 text-ele-body">
                    {v.retiredAt
                      ? `Retired ${formatDate(v.retiredAt)}${v.retiredReason ? `: ${v.retiredReason}` : ""}`
                      : v.approvedAt
                        ? `Approved ${formatDate(v.approvedAt)}${v.approvedByName ? ` by ${v.approvedByName}` : ""}`
                        : "-"}
                  </td>
                  <td className="truncate px-3 py-2 text-ele-body" title={usageSentence(v.usage)}>
                    {v.usage.drafts}
                  </td>
                  <td className="truncate px-3 py-2 text-ele-strong">{percent(v.usage.goodRate)}</td>
                  <td className="max-md:hidden truncate px-3 py-2">{v.usage.flagged ? <StatusPill word="Flagged" tone="amber" title={v.usage.flagReason ?? undefined} /> : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className={sectionCls} aria-label="Source replies">
        <h2 className="mb-2 font-heading text-lg text-ele-strong">The sent replies it came from</h2>
        <SourceReplies replies={detail.sourceReplies} />
      </section>

      {detail.proposals.length > 0 && (
        <section className={sectionCls} aria-label="Proposals for this template">
          <h2 className="mb-2 font-heading text-lg text-ele-strong">Proposals for this template</h2>
          <ul className="space-y-1 text-sm">
            {detail.proposals.map((p) => (
              <li key={p.id} className="text-ele-body">
                {PROPOSAL_KIND_LABEL[p.kind]}: <span className="text-ele-strong">{p.title}</span> ({PROPOSAL_STATUS_LABEL[p.status]}, {formatDate(p.createdAt)})
              </li>
            ))}
          </ul>
          <Link to={`${BASE}?tab=proposals`} className={`mt-2 inline-block ${linkBtn}`}>
            Open the proposals queue
          </Link>
        </section>
      )}

      <ConfirmDeleteModal
        open={deleting}
        variant="destructive"
        title={`Delete version ${t.version} of ${t.key}?`}
        message={
          t.status === "draft"
            ? "This removes this draft version. No draft reply has used it. This cannot be undone."
            : "This removes this version. No draft reply has used it. This cannot be undone."
        }
        confirmLabel="Delete version"
        busy={busy === "delete"}
        error={deleteError}
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          if (busy !== "delete") {
            setDeleting(false);
            setDeleteError(null);
          }
        }}
      />
    </div>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className={labelCls}>{label}</dt>
      <dd className="mt-0.5 whitespace-pre-wrap break-words text-ele-strong">{value}</dd>
    </div>
  );
}

function UsageGrid({ usage }: { usage: ReplyTemplateDetail["usage"] }) {
  const cells: [string, string][] = [
    ["Drafts made", String(usage.drafts)],
    ["Sent unchanged", String(usage.unchanged)],
    ["Small edits", String(usage.smallEdits)],
    ["Rewritten", String(usage.heavilyRewritten)],
    ["Discarded", String(usage.discarded)],
    ["Not yet sent", String(usage.notYetSent)],
    ["Good rate", percent(usage.goodRate)],
  ];
  return (
    <>
      <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4 lg:grid-cols-7" data-testid="usage-grid">
        {cells.map(([k, v]) => (
          <div key={k} className="rounded-ele border border-ele-hairline p-2">
            <dt className="text-xs text-ele-body">{k}</dt>
            <dd className="font-heading text-lg text-ele-strong">{v}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-2 text-xs text-ele-body">
        The good rate is sent unchanged or with small edits over its last {usage.judged || "few"} settled uses. Below half, once it has at least
        five, the template is flagged for review.
      </p>
    </>
  );
}
